'use client'

import { use, useState } from 'react'
import Link from 'next/link'
import { useAuth } from '@/contexts/AuthContext'
import { TierGate } from '@/components/app'
import { getArtistInitials, getArtistColorVar } from '@/lib/artistAvatar'
import {
  PRE_MODERN_MOVEMENTS,
  MODERN_MOVEMENTS,
  POST_WAR_MOVEMENTS,
  CONTEMPORARY_MOVEMENTS,
} from '@/lib/ontology'

// Combine all movements
const ALL_MOVEMENTS = [
  ...PRE_MODERN_MOVEMENTS,
  ...MODERN_MOVEMENTS,
  ...POST_WAR_MOVEMENTS,
  ...CONTEMPORARY_MOVEMENTS,
]

// Sample data for movements
const movementData: Record<string, {
  volume: string
  lots: string
  change: number
  avgHammer: string
  description: string
}> = {
  'impressionism': {
    volume: '$1.2B',
    lots: '12K',
    change: 4.2,
    avgHammer: '$100K',
    description: 'French movement (1860-1900) capturing light and atmosphere through visible brushstrokes and pure color.',
  },
  'abstract-expressionism': {
    volume: '$890M',
    lots: '8K',
    change: 6.8,
    avgHammer: '$111K',
    description: 'Post-WWII American movement emphasizing spontaneous, emotional expression through abstract forms.',
  },
  'pop-art': {
    volume: '$1.4B',
    lots: '18K',
    change: 8.5,
    avgHammer: '$78K',
    description: 'Movement challenging fine art traditions by incorporating imagery from popular culture and mass media.',
  },
  'surrealism': {
    volume: '$680M',
    lots: '9K',
    change: 3.2,
    avgHammer: '$75K',
    description: 'Movement exploring the subconscious mind through dreamlike imagery and unexpected juxtapositions.',
  },
  'neo-expressionism': {
    volume: '$520M',
    lots: '6K',
    change: 12.4,
    avgHammer: '$87K',
    description: '1980s movement reviving expressive, figurative painting with raw, emotional intensity.',
  },
  'street-art': {
    volume: '$380M',
    lots: '24K',
    change: 18.6,
    avgHammer: '$16K',
    description: 'Contemporary movement rooted in graffiti, murals, and public interventions.',
  },
  'minimalism': {
    volume: '$420M',
    lots: '5K',
    change: 2.1,
    avgHammer: '$84K',
    description: 'Movement reducing art to essential geometric forms, emphasizing objectivity and industrial materials.',
  },
  'cubism': {
    volume: '$780M',
    lots: '4K',
    change: 1.8,
    avgHammer: '$195K',
    description: 'Revolutionary movement depicting subjects from multiple viewpoints in fragmented, geometric forms.',
  },
}

const topArtistsByMovement: Record<string, { name: string; slug: string; volume: string; change: number; tier: string }[]> = {
  'impressionism': [
    { name: 'Claude Monet', slug: 'claude-monet', volume: '$320M', change: 5.2, tier: 'Blue-chip' },
    { name: 'Pierre-Auguste Renoir', slug: 'pierre-auguste-renoir', volume: '$180M', change: 2.8, tier: 'Blue-chip' },
    { name: 'Edgar Degas', slug: 'edgar-degas', volume: '$145M', change: 4.1, tier: 'Blue-chip' },
    { name: 'Camille Pissarro', slug: 'camille-pissarro', volume: '$68M', change: 3.2, tier: 'Blue-chip' },
    { name: 'Berthe Morisot', slug: 'berthe-morisot', volume: '$42M', change: 12.4, tier: 'Blue-chip' },
  ],
  'abstract-expressionism': [
    { name: 'Mark Rothko', slug: 'mark-rothko', volume: '$245M', change: 8.4, tier: 'Blue-chip' },
    { name: 'Willem de Kooning', slug: 'willem-de-kooning', volume: '$180M', change: 4.2, tier: 'Blue-chip' },
    { name: 'Jackson Pollock', slug: 'jackson-pollock', volume: '$156M', change: 6.8, tier: 'Blue-chip' },
    { name: 'Franz Kline', slug: 'franz-kline', volume: '$78M', change: 5.1, tier: 'Blue-chip' },
    { name: 'Helen Frankenthaler', slug: 'helen-frankenthaler', volume: '$45M', change: 18.2, tier: 'Blue-chip' },
  ],
  'pop-art': [
    { name: 'Andy Warhol', slug: 'andy-warhol', volume: '$420M', change: 6.8, tier: 'Blue-chip' },
    { name: 'Roy Lichtenstein', slug: 'roy-lichtenstein', volume: '$186M', change: 4.5, tier: 'Blue-chip' },
    { name: 'Jasper Johns', slug: 'jasper-johns', volume: '$124M', change: 2.1, tier: 'Blue-chip' },
    { name: 'Robert Rauschenberg', slug: 'robert-rauschenberg', volume: '$89M', change: 3.8, tier: 'Blue-chip' },
    { name: 'Ed Ruscha', slug: 'ed-ruscha', volume: '$72M', change: 8.2, tier: 'Blue-chip' },
  ],
  'neo-expressionism': [
    { name: 'Jean-Michel Basquiat', slug: 'jean-michel-basquiat', volume: '$285M', change: 12.1, tier: 'Blue-chip' },
    { name: 'Georg Baselitz', slug: 'georg-baselitz', volume: '$68M', change: 4.2, tier: 'Blue-chip' },
    { name: 'Anselm Kiefer', slug: 'anselm-kiefer', volume: '$56M', change: 8.5, tier: 'Blue-chip' },
    { name: 'Julian Schnabel', slug: 'julian-schnabel', volume: '$32M', change: 15.2, tier: 'Blue-chip' },
    { name: 'Francesco Clemente', slug: 'francesco-clemente', volume: '$24M', change: 6.8, tier: 'Blue-chip' },
  ],
  'street-art': [
    { name: 'Banksy', slug: 'banksy', volume: '$98M', change: 15.2, tier: 'Blue-chip' },
    { name: 'Kaws', slug: 'kaws', volume: '$86M', change: -8.4, tier: 'Blue-chip' },
    { name: 'Invader', slug: 'invader', volume: '$24M', change: 22.1, tier: 'Mid-market' },
    { name: 'Shepard Fairey', slug: 'shepard-fairey', volume: '$18M', change: 12.4, tier: 'Mid-market' },
    { name: 'Mr. Brainwash', slug: 'mr-brainwash', volume: '$12M', change: -4.2, tier: 'Mid-market' },
  ],
}

const mediumBreakdown = [
  { medium: 'Paintings', volume: '$4.2B', share: 52, change: 8.4 },
  { medium: 'Prints', volume: '$1.8B', share: 22, change: 6.2 },
  { medium: 'Drawings', volume: '$1.2B', share: 15, change: 4.1 },
  { medium: 'Sculpture', volume: '$0.9B', share: 11, change: 5.8 },
]

// Geographic insights
const geographicInsights = [
  { region: 'Americas', volume: '$2.8B', share: 35, change: 8.4, topCity: 'New York' },
  { region: 'Europe', volume: '$2.4B', share: 30, change: 5.2, topCity: 'London' },
  { region: 'Asia-Pacific', volume: '$2.2B', share: 28, change: 16.8, topCity: 'Hong Kong' },
  { region: 'Middle East', volume: '$0.6B', share: 7, change: 22.1, topCity: 'Dubai' },
]

// Market trends
const marketTrends = [
  { trend: 'Museum acquisitions driving demand', change: 24, direction: 'up' as const },
  { trend: 'Blue-chip works outperforming', change: 18, direction: 'up' as const },
  { trend: 'Private sales increasing', change: 15, direction: 'up' as const },
  { trend: 'Edition multiples softening', change: -6, direction: 'down' as const },
]

// Upcoming auctions
const upcomingAuctions = [
  { house: "Christie's", title: 'Impressionist & Modern Art Evening Sale', date: 'Mar 12, 2026', estimate: '$180-250M', lots: 42, location: 'New York' },
  { house: "Sotheby's", title: 'Modern Art Day Sale', date: 'Mar 14, 2026', estimate: '$65-85M', lots: 156, location: 'London' },
  { house: 'Phillips', title: '20th Century Art', date: 'Mar 20, 2026', estimate: '$40-60M', lots: 78, location: 'Hong Kong' },
  { house: 'Bonhams', title: 'Post-War & Contemporary', date: 'Mar 28, 2026', estimate: '$20-30M', lots: 102, location: 'Los Angeles' },
]

// Recent sales
const recentSales = [
  { artist: 'Claude Monet', work: 'Nymphéas', house: "Christie's", hammer: '$74M', estimate: '$65-85M', premium: '+12%', date: '3 days ago' },
  { artist: 'Pablo Picasso', work: 'Femme assise', house: "Sotheby's", hammer: '$42.8M', estimate: '$40-50M', premium: '+7%', date: '5 days ago' },
  { artist: 'Andy Warhol', work: 'Shot Sage Blue Marilyn', house: "Christie's", hammer: '$28.5M', estimate: '$25-35M', premium: '+14%', date: '1 week ago' },
  { artist: 'Mark Rothko', work: 'No. 7', house: "Sotheby's", hammer: '$18.2M', estimate: '$15-20M', premium: '+21%', date: '1 week ago' },
  { artist: 'Jackson Pollock', work: 'Number 17A', house: 'Phillips', hammer: '$15.8M', estimate: '$12-18M', premium: '+5%', date: '2 weeks ago' },
]

// Artist avatar component
function ArtistAvatar({ name, size = 'md' }: { name: string; size?: 'sm' | 'md' | 'lg' }) {
  const initials = getArtistInitials(name)
  const colorVar = getArtistColorVar(name)

  const sizeClasses = {
    sm: 'w-8 h-8 text-xs',
    md: 'w-10 h-10 text-sm',
    lg: 'w-14 h-14 text-base',
  }

  return (
    <div
      className={`${sizeClasses[size]} rounded-lg flex items-center justify-center font-medium text-white flex-shrink-0`}
      style={{ background: `var(${colorVar})` }}
    >
      {initials}
    </div>
  )
}

function ShareButton({ title }: { title?: string }) {
  const [copied, setCopied] = useState(false)

  const handleShare = async () => {
    const shareUrl = window.location.href
    const shareTitle = title || 'Movement - salle.art'

    if (navigator.share) {
      try {
        await navigator.share({ title: shareTitle, url: shareUrl })
      } catch (err) {}
    } else {
      await navigator.clipboard.writeText(shareUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  return (
    <button
      onClick={handleShare}
      className="pill text-sm flex items-center gap-2"
      style={{ background: 'transparent', border: '1px solid var(--border-standard)', color: 'var(--text-primary)' }}
    >
      {copied ? (
        <>
          <svg className="w-4 h-4 text-positive" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 13l4 4L19 7" />
          </svg>
          Copied!
        </>
      ) : (
        <>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
          </svg>
          Share
        </>
      )}
    </button>
  )
}

export default function MovementPage({ params }: { params: Promise<{ id: string }> }) {
  const { id } = use(params)
  const { canExport } = useAuth()

  // Find movement from ontology
  const movementInfo = ALL_MOVEMENTS.find(m => m.id === id)
  const movementName = movementInfo?.name || id.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  const periodName = movementInfo?.period?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Various'
  const dateRange = movementInfo?.startYear
    ? `${movementInfo.startYear}${movementInfo.endYear ? `–${movementInfo.endYear}` : '–present'}`
    : ''

  const data = movementData[id] || {
    volume: '$200M',
    lots: '5K',
    change: 5.0,
    avgHammer: '$40K',
    description: `Works associated with the ${movementName} movement.`,
  }

  const topArtists = topArtistsByMovement[id] || topArtistsByMovement['pop-art'] || []

  // AI outlook
  const aiOutlook = {
    summary: `${movementName} shows ${data.change >= 0 ? 'positive' : 'negative'} momentum with ${Math.abs(data.change)}% ${data.change >= 0 ? 'growth' : 'decline'} YoY. ${data.change >= 10 ? 'Strong institutional and collector interest is driving demand.' : data.change >= 0 ? 'Steady collector interest with selective buying in quality works.' : 'Market correction offers potential entry points for patient collectors.'}`,
    recommendation: data.change >= 0
      ? `Consider works by established ${movementName} artists with museum representation and strong auction history.`
      : `Focus on documented, exhibited works - provenance and scholarship are critical for value retention.`,
  }

  return (
    <div className="px-4 md:px-6 py-6 max-w-[1400px] mx-auto">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-text-tertiary mb-6">
        <Link href="/app/market" className="hover:text-text-secondary transition-colors">
          Market
        </Link>
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        <span className="text-text-secondary">Movement</span>
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        <span className="text-text-primary">{movementName}</span>
      </div>

      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="font-display text-3xl md:text-4xl font-light">{movementName}</h1>
              {dateRange && (
                <span className="text-sm px-3 py-1 rounded-full bg-page text-text-tertiary">
                  {dateRange}
                </span>
              )}
            </div>
            <p className="text-text-secondary max-w-xl mb-2">{data.description}</p>
            <p className="text-sm text-text-tertiary">
              Period: <Link href={`/app/market/${movementInfo?.period}`} className="text-accent-primary hover:underline">{periodName}</Link>
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <ShareButton title={`${movementName} - salle.art`} />
            <button
              className="pill text-sm flex items-center gap-2"
              style={{ background: 'transparent', border: '1px solid var(--border-standard)', color: 'var(--text-primary)' }}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              Set Alert
            </button>
            <TierGate feature="export" showPrompt={false}>
              <button
                className="pill text-sm flex items-center gap-2"
                style={{ background: 'var(--accent-primary)', color: 'white' }}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Export
              </button>
            </TierGate>
          </div>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="card p-4">
          <p className="text-text-tertiary text-xs mb-1">Total Volume</p>
          <p className="font-display text-2xl font-light">{data.volume}</p>
        </div>
        <div className="card p-4">
          <p className="text-text-tertiary text-xs mb-1">Total Lots</p>
          <p className="font-display text-2xl font-light">{data.lots}</p>
        </div>
        <div className="card p-4">
          <p className="text-text-tertiary text-xs mb-1">YoY Change</p>
          <p
            className="font-display text-2xl font-light"
            style={{ color: data.change >= 0 ? 'var(--positive)' : 'var(--negative)' }}
          >
            {data.change >= 0 ? '+' : ''}{data.change}%
          </p>
        </div>
        <div className="card p-4">
          <p className="text-text-tertiary text-xs mb-1">Avg. Hammer</p>
          <p className="font-display text-2xl font-light">{data.avgHammer}</p>
        </div>
      </div>

      {/* AI Outlook */}
      <div className="card p-5 mb-8 border-l-4" style={{ borderLeftColor: 'var(--accent-primary)' }}>
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'var(--accent-primary)', color: 'white' }}>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="font-medium mb-2">AI Movement Outlook</h3>
            <p className="text-sm text-text-secondary mb-3">{aiOutlook.summary}</p>
            <div className="bg-page rounded-lg p-3">
              <p className="text-xs text-text-tertiary mb-1">Recommendation</p>
              <p className="text-sm text-text-primary">{aiOutlook.recommendation}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Two-column layout: Top Artists + Medium breakdown */}
      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        {/* Top artists - Grid cards */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-medium">Top {movementName} Artists</h2>
            <Link href="/app/artists" className="text-xs text-accent-primary hover:underline">
              View all →
            </Link>
          </div>
          <div className="grid grid-cols-1 gap-3">
            {topArtists.map((artist, i) => (
              <Link
                key={artist.slug}
                href={`/app/artists/${artist.slug}`}
                className="flex items-center justify-between p-3 rounded-xl bg-page hover:bg-card border border-transparent hover:border-border-subtle transition-all"
              >
                <div className="flex items-center gap-3">
                  <span className="text-text-tertiary text-sm w-5">{i + 1}</span>
                  <ArtistAvatar name={artist.name} size="sm" />
                  <div>
                    <p className="font-medium text-sm">{artist.name}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      artist.tier === 'Blue-chip'
                        ? 'bg-accent-primary/10 text-accent-primary'
                        : artist.tier === 'Mid-market'
                        ? 'bg-accent-warm/10 text-accent-warm'
                        : 'bg-positive/10 text-positive'
                    }`}>
                      {artist.tier}
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-medium text-sm tabular-nums">{artist.volume}</p>
                  <p className={`text-xs tabular-nums ${artist.change >= 0 ? 'text-positive' : 'text-negative'}`}>
                    {artist.change >= 0 ? '+' : ''}{artist.change}%
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Medium breakdown - Grid cards */}
        <div className="card p-6">
          <h2 className="font-medium mb-4">By Medium</h2>
          <div className="grid grid-cols-2 gap-3">
            {mediumBreakdown.map((medium) => (
              <Link
                key={medium.medium}
                href={`/app/market/medium/${medium.medium.toLowerCase()}`}
                className="p-4 rounded-xl bg-page hover:bg-card border border-transparent hover:border-border-subtle transition-all"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">{medium.medium}</span>
                  <span className={`text-xs tabular-nums ${medium.change >= 0 ? 'text-positive' : 'text-negative'}`}>
                    {medium.change >= 0 ? '+' : ''}{medium.change}%
                  </span>
                </div>
                <p className="text-xl font-display font-light mb-2">{medium.volume}</p>
                <div className="w-full bg-card rounded-full h-1.5">
                  <div
                    className="h-1.5 rounded-full"
                    style={{ width: `${medium.share}%`, background: 'var(--accent-primary)' }}
                  />
                </div>
                <p className="text-xs text-text-tertiary mt-1">{medium.share}% share</p>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Geographic Insights + Market Trends */}
      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        {/* Geographic Insights */}
        <div className="card p-6">
          <h2 className="font-medium mb-4">Geographic Insights</h2>
          <div className="grid grid-cols-2 gap-3">
            {geographicInsights.map((region) => (
              <div key={region.region} className="p-4 rounded-xl bg-page">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">{region.region}</span>
                  <span className={`text-xs tabular-nums ${region.change >= 0 ? 'text-positive' : 'text-negative'}`}>
                    {region.change >= 0 ? '+' : ''}{region.change}%
                  </span>
                </div>
                <p className="text-lg font-display font-light mb-1">{region.volume}</p>
                <div className="w-full bg-card rounded-full h-1.5 mb-2">
                  <div
                    className="h-1.5 rounded-full"
                    style={{ width: `${region.share}%`, background: 'var(--accent-primary)' }}
                  />
                </div>
                <p className="text-xs text-text-tertiary">
                  {region.share}% · Top: {region.topCity}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Market Trends */}
        <div className="card p-6">
          <h2 className="font-medium mb-4">Market Trends</h2>
          <div className="space-y-3">
            {marketTrends.map((trend, i) => (
              <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-page">
                <div className="flex items-center gap-3">
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      trend.direction === 'up' ? 'bg-positive/10' : 'bg-negative/10'
                    }`}
                  >
                    <svg
                      className={`w-4 h-4 ${trend.direction === 'up' ? 'text-positive' : 'text-negative rotate-180'}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                    </svg>
                  </div>
                  <span className="text-sm">{trend.trend}</span>
                </div>
                <span className={`text-sm font-medium tabular-nums ${trend.change >= 0 ? 'text-positive' : 'text-negative'}`}>
                  {trend.change >= 0 ? '+' : ''}{trend.change}%
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Upcoming Auctions + Recent Sales */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Upcoming Auctions */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-medium">Upcoming Auctions</h2>
            <Link href="/app/market" className="text-xs text-accent-primary hover:underline">
              View all →
            </Link>
          </div>
          <div className="space-y-3">
            {upcomingAuctions.map((auction, i) => (
              <div
                key={i}
                className="p-3 rounded-xl bg-page hover:bg-card transition-colors cursor-pointer"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-accent-primary/10 text-accent-primary">
                        {auction.house}
                      </span>
                      <span className="text-xs text-text-tertiary">{auction.location}</span>
                    </div>
                    <p className="text-sm font-medium">{auction.title}</p>
                    <p className="text-xs text-text-tertiary">{auction.date} · {auction.lots} lots</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-medium tabular-nums">{auction.estimate}</p>
                    <p className="text-xs text-text-tertiary">Estimate</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Sales */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-medium">Recent Sales</h2>
            <Link href="/app/market" className="text-xs text-accent-primary hover:underline">
              View all →
            </Link>
          </div>
          <div className="space-y-3">
            {recentSales.map((sale, i) => (
              <div
                key={i}
                className="flex items-center gap-3 p-3 rounded-xl bg-page hover:bg-card transition-colors cursor-pointer"
              >
                <ArtistAvatar name={sale.artist} size="lg" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-accent-primary/10 text-accent-primary">
                      {sale.house}
                    </span>
                    <span className="text-xs text-text-tertiary">{sale.date}</span>
                  </div>
                  <p className="text-sm font-medium truncate">{sale.work}</p>
                  <p className="text-xs text-text-tertiary">{sale.artist}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-sm font-medium tabular-nums">{sale.hammer}</p>
                  <p className={`text-xs tabular-nums ${sale.premium.startsWith('+') ? 'text-positive' : 'text-negative'}`}>
                    {sale.premium} vs est.
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
